import { useCallback, useMemo, useState } from "react";

/**
 * Generic multi-select helper for list pages.
 *
 *   const { selected, isSelected, toggle, toggleAll, allSelected, someSelected, count, clear }
 *     = useSelection(items, (i) => i.id);
 *
 * Selection state is kept as a Set<string> of ids.  When the underlying
 * `items` list changes (filter, reload), the previously selected ids that no
 * longer exist are silently dropped from any reads, so the UI never claims
 * a phantom row is "selected".
 */
export function useSelection(items, getId = (i) => i.id) {
  const [selected, setSelected] = useState(() => new Set());

  const visibleIds = useMemo(
    () => (items || []).map((i) => getId(i)).filter(Boolean),
    [items, getId]
  );

  const effectiveSelected = useMemo(() => {
    const next = new Set();
    for (const id of visibleIds) {
      if (selected.has(id)) next.add(id);
    }
    return next;
  }, [selected, visibleIds]);

  const isSelected = useCallback((id) => effectiveSelected.has(id), [effectiveSelected]);

  const toggle = useCallback((id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setSelected((prev) => {
      const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => prev.has(id));
      if (allVisibleSelected) {
        const next = new Set(prev);
        for (const id of visibleIds) next.delete(id);
        return next;
      }
      const next = new Set(prev);
      for (const id of visibleIds) next.add(id);
      return next;
    });
  }, [visibleIds]);

  const clear = useCallback(() => setSelected(new Set()), []);

  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => effectiveSelected.has(id));
  const someSelected = !allSelected && effectiveSelected.size > 0;

  return {
    selected: effectiveSelected,
    selectedIds: Array.from(effectiveSelected),
    isSelected,
    toggle,
    toggleAll,
    clear,
    count: effectiveSelected.size,
    allSelected,
    someSelected,
  };
}
